import { createContext, ReactNode, useContext } from 'react';
import {
  useProwlConversation,
  UseProwlConversationReturn,
} from './useProwlConversation';

/**
 * Single Prowl conversation shared by every consumer on the page: the
 * floating ProwlChatWidget (mounted twice, once per layout — see
 * DefaultLayout.tsx / CustomerLayout.tsx), the homepage "Every job, matched
 * by AI" concierge panel, and the search-results page's inline conversation
 * panel.
 *
 * Mounted once at the app root (App.tsx), above the router, so a route
 * change does NOT unmount it — this is what makes a conversation started on
 * the homepage still be there on /services/search-list instead of vanishing
 * the moment it becomes useful.
 */
const ProwlSessionContext = createContext<UseProwlConversationReturn | null>(
  null
);

export function ProwlSessionProvider({ children }: { children: ReactNode }) {
  // Customer portal only — persona is fixed. gigmosaic-provider-dashboard
  // owns its own greenfield widget (design-spec §13) with persona: 'provider'.
  const conversation = useProwlConversation({ persona: 'customer' });
  return (
    <ProwlSessionContext.Provider value={conversation}>
      {children}
    </ProwlSessionContext.Provider>
  );
}

export function useProwlSession(): UseProwlConversationReturn {
  const ctx = useContext(ProwlSessionContext);
  if (!ctx) {
    throw new Error(
      'useProwlSession must be used within a ProwlSessionProvider'
    );
  }
  return ctx;
}
